import { useMemo, useState } from "react";
import { Link } from "react-router";
import { ExternalLink, RefreshCw, Search, TriangleAlert } from "lucide-react";

import { DataTable } from "../components/DataTable.jsx";
import { FilterBar } from "../components/FilterBar.jsx";
import { PageHeader } from "../components/PageHeader.jsx";
import { ProjectIcon } from "../components/ProjectIcon.jsx";
import { StatSummary } from "../components/StatSummary.jsx";
import { EmptyState, Spinner, ago, useToast } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useLive } from "../lib/events.js";
import { useUrlStates } from "../lib/useUrlState.js";

/**
 * What this box serves, read from nginx rather than from a list kept by hand.
 *
 * Keyed by domain rather than by site, because a site can serve several names and
 * a certificate can cover several — the domain is the thing you look up when
 * something is wrong with it.
 *
 * "Answers" is the column that earns the page. PM2 will tell you a process is
 * online while nginx returns 502 to everyone it serves; this asked the domain and
 * reports what it said. The request goes to nginx on this box rather than to the
 * public name, so Cloudflare cannot answer on behalf of an origin that is dead.
 *
 * The certificate column says which certificate, because there are three and they
 * disagree. Seven origin certificates here are months expired while every browser
 * shows a valid one: Cloudflare terminates TLS in front and does not check what
 * the origin offers. That is a renewal that stopped — worth fixing, and one
 * Cloudflare setting away from an outage — but it is not a site that is down, and
 * a red badge saying "expired" reads as though it were.
 */

function answerBadge(check) {
    if (!check) return <span className="badge-queued" title="Not checked yet">—</span>;
    if (check.ok) {
        return (
            <span className="badge-success" title={`${check.status} in ${check.ms}ms, checked ${ago(check.at)}`}>
                {check.status} · {check.ms}ms
            </span>
        );
    }
    return (
        <span className="badge-failed" title={check.error ?? `nginx answered ${check.status}`}>
            {check.status ?? "no answer"} · {ago(check.since)}
        </span>
    );
}

/**
 * The certificate, and whose certificate it is.
 *
 * What was presented on the wire beats what is on disk. Expired behind a proxy is
 * amber rather than red: visitors are getting the proxy's certificate and are
 * unaffected, so it is a renewal to fix rather than an outage to wake up for.
 */
function certBadge(row) {
    const cert = row.served ?? row.cert;
    if (!cert) return <span className="badge-queued">none</span>;

    // Red is for a certificate a visitor actually receives. Behind a proxy they get
    // the proxy's; with no DNS they reach nothing at all. Unknown stays red, because
    // not having looked is not the same as having looked and found it harmless.
    const reaching = row.points === "here" || row.points == null;
    const days = cert.daysLeft;

    if (days < 0) {
        return !reaching
            ? (
                <span
                    className="badge-warn"
                    title={
                        row.points === "proxied"
                            ? `The certificate nginx serves expired ${Math.abs(days)} days ago. This name resolves to a proxy, which presents its own certificate, so visitors are not seeing this one — but renewal has stopped, and it breaks the moment the proxy is set to verify the origin.`
                            : `The certificate nginx serves expired ${Math.abs(days)} days ago. This name does not resolve, so nobody is receiving it either way.`
                    }
                >
                    origin expired {Math.abs(days)}d
                </span>
            )
            : <span className="badge-failed" title={`Expired ${Math.abs(days)} days ago and reaching visitors directly.`}>expired {Math.abs(days)}d ago</span>;
    }

    if (days < 21) return <span className="badge-warn">{days}d left</span>;
    return <span className="badge-success">{days}d left</span>;
}

export function Domains() {
    const [data, setData] = useState(null);
    const [checking, setChecking] = useState(false);
    const [{ q: query, tls, owner, health }, setUrl] = useUrlStates({ q: "", tls: "", owner: "", health: "" });
    const setQuery = (value) => setUrl("q", value);
    const setTls = (value) => setUrl("tls", value);
    const setOwner = (value) => setUrl("owner", value);
    const setHealth = (value) => setUrl("health", value);
    const toast = useToast();

    const load = () => api.infra.domains().then(setData).catch(() => setData({ domains: [], orphanCerts: [], summary: {} }));

    // Whether a domain answers, and what certificate it served, is written by
    // the monitor's pass — and read again when the pass says it is done.
    useLive(["status", "projects"], load);

    const shown = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return (data?.domains ?? []).filter((row) => {
            if (needle && !`${row.domain} ${row.site} ${row.slug ?? ""}`.toLowerCase().includes(needle)) return false;
            const cert = row.served ?? row.cert;
            if (tls === "expired" && !(cert && cert.daysLeft < 0)) return false;
            if (tls === "exposed" && !(cert && cert.daysLeft < 0 && row.points !== "proxied")) return false;
            if (tls === "soon" && !(cert && cert.daysLeft >= 0 && cert.daysLeft < 21)) return false;
            if (tls === "none" && cert) return false;
            if (owner === "__unowned" && row.slug) return false;
            if (owner && owner !== "__unowned" && row.slug !== owner) return false;
            if (health === "down" && row.check?.ok !== false) return false;
            if (health === "up" && row.check?.ok !== true) return false;
            if (health === "nodns" && row.points !== "unresolved") return false;
            return true;
        });
    }, [data, query, tls, owner, health]);

    if (!data) return <Spinner className="muted" />;

    const filtered = Boolean(query || tls || owner || health);
    const owners = [...new Set(data.domains.map((row) => row.slug).filter(Boolean))].sort();

    return (
        <div className="space-y-4">
            <PageHeader
                title="Domains"
                meta={`${data.summary.total} served`}
                actions={
                    <button
                        className="btn-ghost"
                        disabled={checking}
                        onClick={async () => {
                            setChecking(true);
                            try {
                                const result = await api.infra.checkDomains();
                                await load();
                                toast(`Asked ${result.checked} domains · ${result.down} not answering`, result.down > 0 ? "error" : "info");
                            } catch (error) {
                                toast(error.message, "error");
                            } finally {
                                setChecking(false);
                            }
                        }}
                    >
                        <RefreshCw size={14} className={checking ? "animate-spin" : ""} /> {checking ? "Checking…" : "Check now"}
                    </button>
                }
            />

            <StatSummary
                stats={[
                    { label: "Domains", value: data.summary.total },
                    { label: "Not answering", value: data.summary.notAnswering ?? 0, tone: data.summary.notAnswering > 0 ? "red" : null },
                    {
                        label: "Expired certs",
                        value: data.summary.expired,
                        // Red only when one of them is actually reaching visitors.
                        tone: data.summary.expiredExposed > 0 ? "red" : data.summary.expired > 0 ? "amber" : null
                    },
                    // nginx serves it and DNS points nowhere: nobody reaches it at all.
                    { label: "No DNS", value: data.summary.unresolved ?? 0, tone: data.summary.unresolved > 0 ? "amber" : null },
                    { label: "No project", value: data.summary.unowned, tone: data.summary.unowned > 0 ? "amber" : null }
                ]}
            />

            <FilterBar
                query={query}
                onQuery={setQuery}
                placeholder="Search domain, site or project"
                count={filtered ? `${shown.length} of ${data.domains.length}` : `${data.domains.length} domains`}
                filters={[
                    {
                        key: "health",
                        label: "Answering",
                        value: health,
                        onChange: setHealth,
                        options: [
                            { value: "down", label: "Not answering" },
                            { value: "up", label: "Answering" },
                            { value: "nodns", label: "No DNS" }
                        ]
                    },
                    {
                        key: "tls",
                        label: "Certificate",
                        value: tls,
                        onChange: setTls,
                        options: [
                            { value: "expired", label: "Expired" },
                            { value: "exposed", label: "Expired and exposed" },
                            { value: "soon", label: "Expiring soon" },
                            { value: "none", label: "No certificate" }
                        ]
                    },
                    {
                        key: "owner",
                        label: "Project",
                        value: owner,
                        onChange: setOwner,
                        options: [{ value: "__unowned", label: "No project" }, ...owners.map((value) => ({ value, label: value }))]
                    }
                ]}
            />

            <DataTable
                rows={shown}
                rowKey={(row) => `${row.site}-${row.domain}`}
                caption="Domains served by this box"
                empty={<div className="card"><EmptyState icon={Search} title="No domains match" hint="Try a different search, or clear the filters." /></div>}
                columns={[
                    {
                        key: "domain",
                        header: "Domain",
                        primary: true,
                        cell: (row) => (
                            <span className="flex min-w-0 items-center gap-2">
                                <ProjectIcon slug={row.slug} hash={row.faviconHash} size={16} />
                                <a
                                    href={`https://${row.domain}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="link truncate"
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    {row.domain}
                                </a>
                                <ExternalLink size={11} className="muted shrink-0" aria-hidden />
                                {/* nginx serves it; DNS points nowhere. Nobody can reach it. */}
                                {row.points === "unresolved" ? (
                                    <span className="badge-failed shrink-0" title="This name does not resolve, so nothing reaches it however well nginx serves it.">no DNS</span>
                                ) : null}
                            </span>
                        )
                    },
                    { key: "answers", header: "Answers", summary: true, cell: (row) => answerBadge(row.check) },
                    {
                        key: "cert",
                        header: "Certificate",
                        cell: (row) => (
                            <span className="flex flex-wrap items-center gap-1.5">
                                {certBadge(row)}
                                {/* On disk and on the wire disagreeing means one thing. */}
                                {row.certNeedsReload ? (
                                    <span className="badge-warn" title="The file on disk has been renewed but nginx is still serving the old certificate. It needs a reload.">
                                        <TriangleAlert size={11} aria-hidden /> reload
                                    </span>
                                ) : null}
                            </span>
                        )
                    },
                    {
                        key: "project",
                        header: "Project",
                        cell: (row) =>
                            row.slug
                                ? <Link to={`/projects/${row.slug}`} className="link">{row.slug}</Link>
                                : <span className="muted">unassigned</span>
                    },
                    {
                        key: "site",
                        header: "nginx site",
                        hideBelow: "lg",
                        cell: (row) => (
                            <span className="flex items-center gap-2">
                                <code className="font-mono text-xs">{row.site}</code>
                                {/* Meant to be a symlink; a copy diverges silently. */}
                                {row.enabledAsFile ? <span className="badge-warn" title="Enabled as a regular file, not a symlink into sites-available">copy</span> : null}
                            </span>
                        )
                    },
                    {
                        key: "upstreams",
                        header: "Upstreams",
                        hideBelow: "xl",
                        cell: (row) =>
                            row.upstreams.length
                                ? <span className="font-mono text-xs tabular-nums">{row.upstreams.join(", ")}</span>
                                : <span className="muted">static</span>
                    }
                ]}
            />

            {data.orphanCerts.length > 0 ? (
                <section>
                    <h2 className="section-title mb-2">Certificates with nothing serving them</h2>
                    <ul className="card divide-y divide-gray-100 text-sm dark:divide-gray-800">
                        {data.orphanCerts.map((cert) => (
                            <li key={cert.name} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2.5">
                                <span className="font-mono text-xs">{cert.name}</span>
                                <span className="muted min-w-0 flex-1 truncate text-xs">{cert.domains.join(", ")}</span>
                                {certBadge({ cert })}
                            </li>
                        ))}
                    </ul>
                    <p className="muted mt-2 text-xs">
                        These renew on a timer for names no enabled site answers to. Worth removing, or worth re-enabling the site.
                    </p>
                </section>
            ) : null}
        </div>
    );
}
